import { CatalogPlan } from '../models/catalog.plan.model';

const CYCLE_MONTHS: Record<string, number> = {
  monthly: 1,
  quarterly: 3,
  semi_annual: 6,
  yearly: 12,
};

export interface CyclePriceInfo {
  /** The plan's actual price for the selected cycle (undefined if no pricing) */
  periodPrice?: number;
  /** Currency code */
  currencyCode: string;
  /** Monthly equivalent (periodPrice / months). Undefined for one_time or monthly or no pricing */
  monthlyEquivalent?: number;
  /** Savings percentage vs paying monthly for the same plan. Undefined if no monthly ref, or no savings */
  savingsPercent?: number;
  /** Number of months in the cycle (undefined for one_time) */
  cycleMonths?: number;
}

/**
 * Compute cycle pricing info for a plan at a given billing cycle.
 * Single source of truth — used by both SubscriptionOffers and ModalUpgradePlan.
 */
export function getCyclePriceInfo(plan: CatalogPlan, billingCycle: string): CyclePriceInfo {
  const defaultCurrency = 'CHF';

  if (plan.is_free) {
    return { periodPrice: 0, currencyCode: defaultCurrency };
  }

  const pricing = plan.pricing?.find(p => p.billing_cycle === billingCycle);
  const periodPrice = pricing?.price;
  const currencyCode = pricing?.currency?.code ?? defaultCurrency;

  if (periodPrice === undefined || periodPrice === null) {
    return { periodPrice: undefined, currencyCode };
  }

  const months = CYCLE_MONTHS[billingCycle];

  // one_time or unknown cycle — no monthly equivalent
  if (!months || months <= 1) {
    return { periodPrice, currencyCode, cycleMonths: months };
  }

  // Monthly equivalent
  const monthlyEquivalent = Math.round((periodPrice / months) * 100) / 100;

  // Savings vs monthly pricing of the SAME plan
  const monthlyPricing = plan.pricing?.find(p => p.billing_cycle === 'monthly');
  let savingsPercent: number | undefined;

  if (monthlyPricing?.price !== undefined && monthlyPricing.price > 0) {
    const monthlyTotal = monthlyPricing.price * months;
    if (periodPrice < monthlyTotal) {
      savingsPercent = Math.round(((monthlyTotal - periodPrice) / monthlyTotal) * 100);
    }
    // If cycle is more expensive or equal — no savings shown
  }

  return { periodPrice, currencyCode, monthlyEquivalent, savingsPercent, cycleMonths: months };
}
